import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import { errorToast, successToast } from "../../components/Toast";
import GeneralTermsService from "../../services/_generalTerms";
import { getParentProductNameListApi } from "../../services/_product";

interface Product {
  id: string;
  name: string;
}

interface TermSection {
  id: string;
  term: string;
}

const CreateGeneralTerms = () => {
  const navigate = useNavigate();

  const [parentProducts, setParentProducts] = useState<Product[]>([]);
  const [filterProduct, setFilterProduct] = useState<Product | null>(null);
  const [filterTerms, setFilterTerms] = useState<string[]>([]);
  const [selectedFilterTerm, setSelectedFilterTerm] = useState<string | null>(
    null,
  );
  const [sections, setSections] = useState<TermSection[]>([]);
  const [createProduct, setCreateProduct] = useState<Product | null>(null);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);

  // Error state for create product validation
  const [createProductError, setCreateProductError] = useState<string | null>(
    null,
  );

  // Fetch all products
  useEffect(() => {
    const fetchProducts = async () => {
      const result: any = await getParentProductNameListApi("status=true");
      if (result.isSuccess) setParentProducts(result.data);
      else errorToast(result.message);
    };
    fetchProducts();
  }, []);

  // Fetch all general terms initially (before any product is selected)
  useEffect(() => {
    const fetchAllTerms = async () => {
      const result: any = await GeneralTermsService.getAllGeneralTermsApi();
      if (result.isSuccess) setFilterTerms(result.data.map((t: any) => t.term));
      else errorToast(result.message);
    };
    fetchAllTerms();
  }, []);

  // Fetch general terms for selected filter product
  useEffect(() => {
    const fetchTerms = async () => {
      if (!filterProduct) {
        const allTermsResult: any =
          await GeneralTermsService.getAllGeneralTermsApi();
        setFilterTerms(allTermsResult.data.map((t: any) => t.term));
        return; // If no product selected, reset terms
      }
      const result: any =
        await GeneralTermsService.getAllGeneralTermsByProductIdApi(
          `product_id=${filterProduct.id}`,
        );
      if (result.isSuccess) setFilterTerms(result.data.map((t: any) => t.term));
      else errorToast(result.message);
    };
    fetchTerms();
  }, [filterProduct]);

  // Add term from filter to sections
  const handleAddFilterTerm = (term: string) => {
    if (!sections.find((s) => s.term === term)) {
      setSections((prev) => [...prev, { id: Date.now().toString(), term }]);
    }
  };

  // Select All terms from filter
  const handleSelectAllFilterTerms = () => {
    const newSections = filterTerms
      .filter((term) => !sections.find((s) => s.term === term))
      .map((term) => ({ id: Date.now().toString() + Math.random(), term }));
    setSections((prev) => [...prev, ...newSections]);
  };

  // Add new blank section
  const handleAddNewSection = () => {
    setSections((prev) => [...prev, { id: Date.now().toString(), term: "" }]);
  };

  // Remove section
  const handleRemoveSection = (id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
  };

  // Change section value
  const handleSectionChange = (id: string, value: string) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, term: value } : s)),
    );
  };

  // Handle form submit
  const handleSubmit = async () => {
    // Check if product is selected for "Create Product"
    if (!createProduct) {
      setCreateProductError("Please select a product");
      return;
    } else {
      setCreateProductError(null); // Clear error if product is selected
    }

    const termsToSubmit = sections.map((s) => s.term.trim()).filter(Boolean);
    if (termsToSubmit.length === 0) {
      errorToast("Please add at least one term");
      return;
    }

    // Check duplicates
    const uniqueTermsSet = new Set(termsToSubmit.map((t) => t.toLowerCase()));
    if (uniqueTermsSet.size !== termsToSubmit.length) {
      errorToast("Duplicate terms found. Please remove duplicates.");
      return;
    }

    setSubmitLoader(true);
    try {
      const payload = { product_id: createProduct.id, terms: termsToSubmit };
      const result: any =
        await GeneralTermsService.createManyGeneralTermsApi(payload);
      if (result.isSuccess) {
        successToast(`${result.data} General Terms created successfully!`);
        navigate("/general-terms-list");
      } else {
        errorToast(result.message);
      }
    } catch (error: any) {
      errorToast(error.message || "Something went wrong!");
    } finally {
      setSubmitLoader(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header pb-0">
        <h5>Create General Terms</h5>
      </div>
      <div className="card-body">
        {/* Filter Inputs */}
        <div className="row g-3 mb-4">
          <div className="col-lg-4 col-md-6">
            <label className="form-label" style={{ fontSize: "0.9375rem" }}>
              Product (Filter):
            </label>
            <ReactSelect
              isClearable
              options={parentProducts.map((p) => ({
                value: p.id,
                label: p.name,
              }))}
              value={
                filterProduct
                  ? { value: filterProduct.id, label: filterProduct.name }
                  : null
              }
              onChange={(selected: any) =>
                setFilterProduct(
                  selected
                    ? { id: selected.value, name: selected.label }
                    : null,
                )
              }
              placeholder="Please select a product"
            />
          </div>

          <div className="col-lg-8 col-md-6 d-flex align-items-end">
            <div className="flex-grow-1">
              <label className="form-label" style={{ fontSize: "0.9375rem" }}>
                General Terms (Filter):
              </label>
              <ReactSelect
                isClearable
                className="w-90"
                styles={{
                  container: (base) => ({
                    ...base,
                    width: "100%", // FIXED WIDTH – NO SHRINKING
                  }),
                }}
                options={filterTerms.map((term) => ({
                  value: term,
                  label: term,
                }))}
                value={
                  selectedFilterTerm
                    ? { value: selectedFilterTerm, label: selectedFilterTerm }
                    : null
                }
                onChange={(selected) => {
                  if (selected) {
                    setSelectedFilterTerm(selected.value);
                    handleAddFilterTerm(selected.value);
                  } else {
                    setSelectedFilterTerm(null); // Fix: clears the dropdown
                  }
                }}
                placeholder="Please select the general term"
              />
            </div>

            {filterProduct && (
              <button
                type="button"
                className="btn btn-outline-primary ms-2 h-60 px-3"
                style={{ whiteSpace: "nowrap" }}
                onClick={handleSelectAllFilterTerms}
              >
                Select All
              </button>
            )}
          </div>
        </div>

        <hr style={{ marginBottom: 20, marginTop: 30 }} />

        {/* Create General Terms Section */}
        <div className="border p-3 mb-4" style={{ borderRadius: "10px" }}>
          <div className="row mb-3">
            <div className="col-lg-4 col-md-6">
              <label className="form-label" style={{ fontSize: "0.9375rem" }}>
                Product (Create)<span className="text-danger">*</span>
              </label>
              <div className="position-relative">
                <ReactSelect
                  isClearable
                  options={parentProducts.map((p) => ({
                    value: p.id,
                    label: p.name,
                  }))}
                  value={
                    createProduct
                      ? { value: createProduct.id, label: createProduct.name }
                      : null
                  }
                  onChange={(selected: any) =>
                    setCreateProduct(
                      selected
                        ? { id: selected.value, name: selected.label }
                        : null,
                    )
                  }
                  placeholder="Please select a product"
                />
                {/* Inline error message */}
                {createProductError && (
                  <div
                    className={`
                      text-danger mt-1
                      d-block d-md-none
                    `}
                  >
                    {createProductError}
                  </div>
                )}

                {createProductError && (
                  <div
                    className={`
                      text-danger mt-1 position-absolute
                      d-none d-md-block
                    `}
                    style={{ bottom: "-20px", left: 0 }}
                  >
                    {createProductError}
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-8 col-md-6 d-flex align-items-end mt-2 mt-md-0">
              <button
                type="button"
                className="btn btn-outline-success"
                onClick={handleAddNewSection}
              >
                + Add New
              </button>
            </div>
          </div>

          {/* Term Sections */}
          {sections.map((section) => (
            <div key={section.id} className="row g-2 mt-2 align-items-start">
              <div className="col-10 col-md-11 position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter General Term"
                  value={section.term}
                  onChange={(e) =>
                    handleSectionChange(section.id, e.target.value)
                  }
                  style={{ padding: "6px 10px", fontSize: "14px" }}
                />
              </div>
              <div className="col-2 col-md-1 d-flex justify-content-md-start">
                <button
                  type="button"
                  className="btn btn-md btn-danger"
                  onClick={() => handleRemoveSection(section.id)}
                  style={{ width: "100%", maxWidth: 45 }}
                >
                  X
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <div className="mt-10">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitLoader}
          >
            {submitLoader && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
            )}
            Submit
          </button>
          <Link
            to={"/general-terms-list"}
            className="btn btn-label-github ms-3"
          >
            Back
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CreateGeneralTerms;
